import { useTranslation } from 'react-i18next';
import { Platform, Pressable, View } from 'react-native';
import { Button, Modal as ModalRNP, Portal, Text } from 'react-native-paper';
import { useRoleHex } from '../../../themes';
import { themedStyleSheet } from '../../../themes/themedStyleSheet';

export type RoutineDraftModalProps = {
  visible: boolean;
  onClose: () => void;
  onContinueDraft: () => void;
  onStartNew: () => void;
  draft?: any;
  workspaceAsset?: any;
  routine?: any;
  showPortal?: boolean;
  children?: React.ReactNode;
};

export const RoutineDraftModal = ({
  visible,
  onClose,
  onContinueDraft,
  onStartNew,
  draft,
  workspaceAsset: _workspaceAsset,
  routine: _routine,
  showPortal = true,
}: RoutineDraftModalProps) => {
  const textOnInverseHex = useRoleHex('textOnInverse');
  const textStrongHex = useRoleHex('textStrong');
  const { t } = useTranslation();

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    }).format(date);
  };

  const getDraftMessage = () => {
    if (!draft) {
      return t('routines.draftModal.messageNoDraft');
    }

    // Get date even if createdBy is missing
    const savedDate = draft.createdAt
      ? formatDate(new Date(draft.createdAt))
      : 'Unknown date';

    // If no createdBy info, show simplified message
    if (!draft.createdBy || !draft.createdBy.name) {
      return t('routines.draftModal.messageWithoutCreator', {
        date: savedDate,
      });
    }

    const firstName = draft.createdBy.name?.first || '';
    const lastName = draft.createdBy.name?.last || '';
    const creatorName = `${firstName} ${lastName}`.trim() || 'Unknown user';

    return t('routines.draftModal.messageWithCreator', {
      creator: creatorName,
      date: savedDate,
    });
  };

  const handleContinueDraft = () => {
    onContinueDraft();
    // Don't call onClose() - let the handlers manage state
  };

  const handleStartNew = () => {
    onStartNew();
    // Don't call onClose() - let the handlers manage state
  };

  return (
    <ContainerModal
      visible={visible}
      onClose={onClose}
      onContinueDraft={onContinueDraft}
      onStartNew={onStartNew}
      draft={draft}
      workspaceAsset={_workspaceAsset}
      routine={_routine}
      showPortal={showPortal}
    >
      <View style={styles.titleContainer}>
        <Text style={styles.title}>{getDraftMessage()}</Text>
      </View>
      <Button
        style={[styles.button, styles.primaryButton]}
        textColor={textOnInverseHex}
        labelStyle={styles.buttonLabel}
        onPress={handleContinueDraft}
      >
        {t('routines.draftModal.continueDraft')}
      </Button>
      <Button
        style={[styles.button, styles.tertiaryButton]}
        textColor={textStrongHex}
        labelStyle={styles.buttonLabel}
        onPress={handleStartNew}
      >
        {t('routines.draftModal.startNew')}
      </Button>
      <Button
        style={[styles.button, styles.secondaryButton]}
        textColor={textStrongHex}
        labelStyle={styles.buttonLabel}
        onPress={onClose}
      >
        {t('routines.draftModal.cancel')}
      </Button>
    </ContainerModal>
  );
};

const ContainerModal = (props: RoutineDraftModalProps) => {
  if (props.showPortal) {
    return (
      <Portal>
        <ModalRNP
          visible={props.visible}
          onDismiss={props.onClose}
          dismissable={false}
          contentContainerStyle={styles.containerStyle}
        >
          {props?.children}
        </ModalRNP>
      </Portal>
    );
  }

  // Inline: absolutely-positioned overlay scoped to the nearest positioned parent
  if (!props.visible) {
    return null;
  }

  return (
    <Pressable style={styles.inlineBackdrop} onPress={props.onClose}>
      <Pressable
        style={styles.containerStyle}
        onPress={(e) => e.stopPropagation()}
      >
        {props.children}
      </Pressable>
    </Pressable>
  );
};
const styles = themedStyleSheet((role) => ({
  inlineBackdrop: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: role.backdrop,
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  containerStyle: {
    backgroundColor: role.surface,
    paddingHorizontal: 20,
    paddingVertical: 24,
    marginHorizontal: 20,
    borderRadius: 12,
    minWidth: 350,
    justifyContent: 'flex-start',
    ...Platform.select({
      web: {
        maxWidth: 500,
      },
      default: {
        alignSelf: 'center',
      },
    }),
  },
  titleContainer: {
    marginBottom: 24,
    flexDirection: 'row',
    justifyContent: 'center',
  },
  title: {
    fontSize: 16,
    fontWeight: '400',
    textAlign: 'center',
    flexWrap: 'wrap',
    flexShrink: 1,
    color: role.textStrong,
  },
  button: {
    borderWidth: 1,
    borderRadius: 8,
    height: 44,
    justifyContent: 'center',
  },
  // No `color` here on purpose: Paper applies `labelStyle` after the style it
  // derives from `textColor`, so a colour set here would override every
  // button's own `textColor`. The primary button sits on `surfaceInverse`,
  // which is white in dark mode, and needs `textOnInverse` rather than the
  // `textStrong` the other two use.
  buttonLabel: {
    fontSize: 16,
    fontWeight: '600',
    textAlignVertical: 'center',
  },
  primaryButton: {
    marginBottom: 12,
    backgroundColor: role.surfaceInverse,
    borderColor: role.surfaceInverse,
  },
  tertiaryButton: {
    marginBottom: 12,
    borderColor: role.textStrong,
  },
  secondaryButton: {
    borderWidth: 0,
  },
}));
